import React, { useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { useLocaleContext } from "../context/LocaleContext";
import { locales, urlMappings } from "../utils/hreflang";

// X (Twitter) Icon Component - Modern, lightweight SVG
const XIcon: React.FC<{ size?: number; className?: string }> = React.memo(
  ({ size = 20, className = "" }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
        fill="currentColor"
      />
    </svg>
  )
);

XIcon.displayName = "XIcon";

const Footer: React.FC = React.memo(() => {
  const { locale, changeLocale } = useLocaleContext();

  // Get the home route for the current locale
  const getHomeRoute = useCallback((): string => {
    const homeRoutes: Record<string, string> = {
      en: "/",
      de: "/de",
      "pt-BR": "/pt-br",
      "ms-MY": "/my",
      es: "/es",
      fr: "/fr",
      ja: "/ja",
      id: "/id",
    };
    return homeRoutes[locale] || "/";
  }, [locale]);

  // Memoize the localized path function
  const getLocalePath = useCallback(
    (path: string) => {
      const prefix = locales[locale] || "";

      // Check if this path has a translation
      const mappings = urlMappings[path];
      if (mappings && mappings[locale]) {
        return `${prefix}${mappings[locale]}`;
      }

      // No translation, use the original path
      return `${prefix}${path}`;
    },
    [locale]
  );

  // Memoize translations object
  const translations: Record<string, Record<string, string>> = useMemo(
    () => ({
      description: {
        en: "Remove invisible AI watermarks from text and documents.",
        de: "Entfernen Sie unsichtbare KI-Wasserzeichen aus Text und Dokumenten.",
        "pt-BR":
          "Remova marcas d'água invisíveis de IA de textos e documentos.",
        "ms-MY":
          "Buang tanda air AI yang tidak kelihatan daripada teks dan dokumen.",
        es: "Elimina marcas de agua de IA invisibles de texto y documentos.",
        fr: "Supprimez les filigranes IA invisibles de vos textes et documents.",
        ja: "テキストや文書から見えないAI透かしを削除します。",
        id: "Hapus watermark AI tak terlihat dari teks dan dokumen.",
      },
      followOnX: {
        en: "Follow on X",
        de: "Folgen auf X",
        "pt-BR": "Seguir no X",
        "ms-MY": "Ikuti di X",
        es: "Seguir en X",
        fr: "Suivre sur X",
        ja: "Xでフォロー",
        id: "Ikuti di X",
      },
      tools: {
        en: "Tools",
        de: "Werkzeuge",
        "pt-BR": "Ferramentas",
        "ms-MY": "Alat",
        es: "Herramientas",
        fr: "Outils",
        ja: "ツール",
        id: "Alat",
      },
      chromeExtension: {
        en: "Chrome Extension",
        de: "Chrome-Erweiterung",
        "pt-BR": "Extensão Chrome",
        "ms-MY": "Sambungan Chrome",
        es: "Extensión Chrome",
        fr: "Extension Chrome",
        ja: "Chrome拡張機能",
        id: "Ekstensi Chrome",
      },
      chatgptWatermarks: {
        en: "ChatGPT Watermarks",
        de: "ChatGPT Wasserzeichen",
        "pt-BR": "Marcas d'Água ChatGPT",
        "ms-MY": "Tanda Air ChatGPT",
        es: "Marcas de Agua ChatGPT",
        fr: "Filigranes ChatGPT",
        ja: "ChatGPT透かし",
        id: "Watermark ChatGPT",
      },
      geminiWatermarks: {
        en: "Gemini Watermarks",
        de: "Gemini Wasserzeichen",
        "pt-BR": "Marcas d'Água Gemini",
        "ms-MY": "Tanda Air Gemini",
        es: "Marcas de Agua Gemini",
        fr: "Filigranes Gemini",
        ja: "Gemini透かし",
        id: "Watermark Gemini",
      },
      geminiImageWatermarks: {
        en: "Gemini Image Watermarks",
        de: "Gemini Bild-Wasserzeichen",
        "pt-BR": "Marcas d'Água de Imagem Gemini",
        "ms-MY": "Tanda Air Imej Gemini",
        es: "Marcas de Agua de Imagen Gemini",
        fr: "Filigranes Image Gemini",
        ja: "Gemini画像透かし",
        id: "Watermark Gambar Gemini",
      },
      claudeWatermarks: {
        en: "Claude Watermarks",
        de: "Claude Wasserzeichen",
        "pt-BR": "Marcas d'Água Claude",
        "ms-MY": "Tanda Air Claude",
        es: "Marcas de Agua Claude",
        fr: "Filigranes Claude",
        ja: "Claude透かし",
        id: "Watermark Claude",
      },
      forStudents: {
        en: "For Students",
        de: "Für Studierende",
        "pt-BR": "Para Estudantes",
        "ms-MY": "Untuk Pelajar",
        es: "Para Estudiantes",
        fr: "Pour les Etudiants",
        ja: "学生向け",
        id: "Untuk Pelajar",
      },
      freeTools: {
        en: "Free Tools",
        de: "Kostenlose Tools",
        "pt-BR": "Ferramentas Grátis",
        "ms-MY": "Alat Percuma",
        es: "Herramientas Gratis",
        fr: "Outils Gratuits",
        ja: "無料ツール",
        id: "Alat Gratis",
      },
      freeChatGPT: {
        en: "Free ChatGPT Remover",
        de: "Kostenloser ChatGPT Entferner",
        "pt-BR": "Removedor ChatGPT Grátis",
        "ms-MY": "Penyingkir ChatGPT Percuma",
        es: "Eliminador ChatGPT Gratis",
        fr: "Suppresseur ChatGPT Gratuit",
        ja: "無料ChatGPT透かし削除",
        id: "Penghapus ChatGPT Gratis",
      },
      freeAIText: {
        en: "Free AI Text Remover",
        de: "Kostenloser KI-Text Entferner",
        "pt-BR": "Removedor de Texto IA Grátis",
        "ms-MY": "Penyingkir Teks AI Percuma",
        es: "Eliminador Texto IA Gratis",
        fr: "Suppresseur Texte IA Gratuit",
        ja: "無料AIテキスト透かし削除",
        id: "Penghapus Teks AI Gratis",
      },
      freeAI: {
        en: "Free AI Remover",
        de: "Kostenloser KI Entferner",
        "pt-BR": "Removedor de IA Grátis",
        "ms-MY": "Penyingkir AI Percuma",
        es: "Eliminador IA Gratis",
        fr: "Suppresseur IA Gratuit",
        ja: "無料AI透かし削除",
        id: "Penghapus AI Gratis",
      },
      freeGPT: {
        en: "Free GPT Remover",
        de: "Kostenloser GPT Entferner",
        "pt-BR": "Removedor GPT Grátis",
        "ms-MY": "Penyingkir GPT Percuma",
        es: "Eliminador GPT Gratis",
        fr: "Suppresseur GPT Gratuit",
        ja: "無料GPT透かし削除",
        id: "Penghapus GPT Gratis",
      },
      resources: {
        en: "Resources",
        de: "Ressourcen",
        "pt-BR": "Recursos",
        "ms-MY": "Sumber",
        es: "Recursos",
        fr: "Ressources",
        ja: "リソース",
        id: "Sumber Daya",
      },
      blog: {
        en: "Blog",
        de: "Blog",
        "pt-BR": "Blog",
        "ms-MY": "Blog",
        es: "Blog",
        fr: "Blog",
        ja: "ブログ",
        id: "Blog",
      },
      knowledge: {
        en: "Knowledge Hub",
        de: "Wissenszentrum",
        "pt-BR": "Centro de Conhecimento",
        "ms-MY": "Pusat Pengetahuan",
        es: "Centro de Conocimiento",
        fr: "Base de Connaissances",
        ja: "ナレッジハブ",
        id: "Pusat Pengetahuan",
      },
      about: {
        en: "About",
        de: "Über uns",
        "pt-BR": "Sobre",
        "ms-MY": "Tentang",
        es: "Acerca de",
        fr: "A propos",
        ja: "概要",
        id: "Tentang",
      },
      legal: {
        en: "Legal",
        de: "Rechtliches",
        "pt-BR": "Legal",
        "ms-MY": "Undang-undang",
        es: "Legal",
        fr: "Mentions Legales",
        ja: "法的情報",
        id: "Hukum",
      },
      allRightsReserved: {
        en: "All rights reserved.",
        de: "Alle Rechte vorbehalten.",
        "pt-BR": "Todos os direitos reservados.",
        "ms-MY": "Hak cipta terpelihara.",
        es: "Todos los derechos reservados.",
        fr: "Tous droits reserves.",
        ja: "全著作権所有。",
        id: "Hak cipta dilindungi.",
      },
      detectedLocation: {
        en: "Detected Location",
        de: "Erkanntes Land",
        "pt-BR": "Localização Detectada",
        "ms-MY": "Lokasi Dikesan",
        es: "Ubicación Detectada",
        fr: "Localisation Detectee",
        ja: "検出された場所",
        id: "Lokasi Terdeteksi",
      },
      pricing: {
        en: "Pricing",
        de: "Preise",
        "pt-BR": "Preços",
        "ms-MY": "Harga",
        es: "Precios",
        fr: "Tarifs",
        ja: "料金",
        id: "Harga",
      },
      faq: {
        en: "FAQ",
        de: "FAQ",
        "pt-BR": "Perguntas Frequentes",
        "ms-MY": "Soalan Lazim",
        es: "Preguntas Frecuentes",
        fr: "FAQ",
        ja: "よくある質問",
        id: "FAQ",
      },
      comparison: {
        en: "Why Remove Watermarks?",
        de: "Warum Wasserzeichen entfernen?",
        "pt-BR": "Por que Remover Marcas d'Água?",
        "ms-MY": "Mengapa Buang Tanda Air?",
        es: "¿Por qué Eliminar Marcas de Agua?",
        fr: "Pourquoi Supprimer les Filigranes?",
        ja: "透かしを削除する理由",
        id: "Mengapa Hapus Watermark?",
      },
      featureRequest: {
        en: "Request Feature",
        de: "Feature anfragen",
        "pt-BR": "Solicitar Recurso",
        "ms-MY": "Minta Ciri",
        es: "Solicitar Función",
        fr: "Demander une Fonctionnalite",
        ja: "機能をリクエスト",
        id: "Minta Fitur",
      },
      devDocs: {
        en: "Developer Docs",
        de: "Developer Docs",
        "pt-BR": "Developer Docs",
        "ms-MY": "Developer Docs",
        es: "Developer Docs",
        fr: "Documentation Developpeur",
        ja: "開発者ドキュメント",
        id: "Dokumentasi Developer",
      },
      api: {
        en: "API",
        de: "API",
        "pt-BR": "API",
        "ms-MY": "API",
        es: "API",
        fr: "API",
        ja: "API",
        id: "API",
      },
      apiOverview: {
        en: "API Overview",
        de: "API Übersicht",
        "pt-BR": "Visão Geral da API",
        "ms-MY": "Gambaran API",
        es: "Descripción de la API",
        fr: "Apercu de l'API",
        ja: "API概要",
        id: "Gambaran API",
      },
      apiWritingTools: {
        en: "For Writing Tools",
        de: "Für Schreibwerkzeuge",
        "pt-BR": "Para Ferramentas de Escrita",
        "ms-MY": "Untuk Alat Penulisan",
        es: "Para Herramientas de Escritura",
        fr: "Pour Outils d'Ecriture",
        ja: "執筆ツール向け",
        id: "Untuk Alat Penulisan",
      },
      apiChatbots: {
        en: "For Chatbots",
        de: "Für Chatbots",
        "pt-BR": "Para Chatbots",
        "ms-MY": "Untuk Chatbot",
        es: "Para Chatbots",
        fr: "Pour Chatbots",
        ja: "チャットボット向け",
        id: "Untuk Chatbot",
      },
      apiContentPlatforms: {
        en: "For Content Platforms",
        de: "Für Content-Plattformen",
        "pt-BR": "Para Plataformas de Conteúdo",
        "ms-MY": "Untuk Platform Kandungan",
        es: "Para Plataformas de Contenido",
        fr: "Pour Plateformes de Contenu",
        ja: "コンテンツプラットフォーム向け",
        id: "Untuk Platform Konten",
      },
      privacy: {
        en: "Privacy Policy",
        de: "Datenschutz",
        "pt-BR": "Política de Privacidade",
        "ms-MY": "Dasar Privasi",
        es: "Política de Privacidad",
        fr: "Politique de Confidentialite",
        ja: "プライバシーポリシー",
        id: "Kebijakan Privasi",
      },
      terms: {
        en: "Terms of Service",
        de: "Nutzungsbedingungen",
        "pt-BR": "Termos de Serviço",
        "ms-MY": "Terma Perkhidmatan",
        es: "Términos de Servicio",
        fr: "Conditions d'Utilisation",
        ja: "利用規約",
        id: "Ketentuan Layanan",
      },
      imprint: {
        en: "Imprint",
        de: "Impressum",
        "pt-BR": "Expediente",
        "ms-MY": "Imprint",
        es: "Aviso Legal",
        fr: "Mentions Legales",
        ja: "運営者情報",
        id: "Imprint",
      },
    }),
    []
  );

  // Memoize getText function
  const getText = useCallback(
    (key: string): string => {
      return translations[key]?.[locale] || translations[key]?.["en"] || "";
    },
    [locale, translations]
  );

  // Removed country detection - no longer needed in footer
  // Auto-locale switching disabled to improve performance

  useEffect(() => {
    const checkLocale = () => {
      try {
        // Auto-switch to German for German-speaking countries
        // Only switch if user hasn't already explicitly chosen a language
        const path = window.location.pathname;
        const hasExplicitLocale =
          path === "/de" || path.startsWith("/de/") ||
          path === "/en" || path.startsWith("/en/");

        // Disabled automatic locale switching - user can manually choose language
        if (false && !hasExplicitLocale) {
          changeLocale("de");
        }
      } catch (error) {
        console.error("[Footer] Failed to get pricing info:", error);
      }
    };

    checkLocale();
  }, [changeLocale]);

  return (
    <footer className="mt-auto py-12 bg-white dark:bg-neutral-950 border-t border-neutral-200 dark:border-neutral-800">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Brand Column */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck
                size={24}
                className="text-accent-600 dark:text-accent-500"
                aria-hidden="true"
              />
              <Link
                to={getHomeRoute()}
                className="font-bold text-lg text-neutral-900 dark:text-white hover:text-accent-600 dark:hover:text-accent-400 transition-colors"
                aria-label="Return to homepage"
              >
                GPT Watermark-Remover
              </Link>
            </div>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
              {getText("description")}
            </p>
            <a
              href="https://x.com/AleksDoesCode"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors"
              aria-label="Follow on X (Twitter)"
            >
              <XIcon size={20} />
              <span>{getText("followOnX")}</span>
            </a>
          </div>

          {/* Tools Column */}
          <div>
            <h2 className="font-semibold text-neutral-900 dark:text-white mb-4">
              {getText("tools")}
            </h2>
            <ul className="space-y-2 text-sm">
              <li>
                <a
                  href="https://chromewebstore.google.com/detail/gpt-watermark-remover/iaekbmafemcgololhmjgppedicomfnil"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-neutral-600 dark:text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors"
                  aria-label="Chrome Extension"
                >
                  {getText("chromeExtension")}
                </a>
              </li>
              <li>
                <Link
                  to={getLocalePath("/remove-chatgpt-watermarks")}
                  className="text-neutral-600 dark:text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors"
                  aria-label="ChatGPT Watermark Remover"
                >
                  {getText("chatgptWatermarks")}
                </Link>
              </li>
              <li>
                <Link
                  to={getLocalePath("/remove-gemini-watermarks")}
                  className="text-neutral-600 dark:text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors"
                  aria-label="Gemini Watermark Remover"
                >
                  {getText("geminiWatermarks")}
                </Link>
              </li>
              <li>
                <Link
                  to={getLocalePath("/remove-gemini-image-watermark")}
                  className="text-neutral-600 dark:text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors"
                  aria-label="Gemini Image Watermark Remover"
                >
                  {getText("geminiImageWatermarks")}
                </Link>
              </li>
              <li>
                <Link
                  to={getLocalePath("/remove-claude-watermarks")}
                  className="text-neutral-600 dark:text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors"
                  aria-label="Claude Watermark Remover"
                >
                  {getText("claudeWatermarks")}
                </Link>
              </li>
              <li>
                <Link
                  to={getLocalePath("/ai-watermark-remover-students")}
                  className="text-neutral-600 dark:text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors"
                  aria-label="AI Watermark Remover for Students"
                >
                  {getText("forStudents")}
                </Link>
              </li>
            </ul>
            {/* Free Tools Subsection */}
            <h3 className="font-semibold text-neutral-900 dark:text-white mt-6 mb-3 text-sm">
              {getText("freeTools")}
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  to={getLocalePath("/free-chatgpt-watermark-remover")}
                  className="text-neutral-600 dark:text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors"
                  aria-label="Free ChatGPT Watermark Remover"
                >
                  {getText("freeChatGPT")}
                </Link>
              </li>
              <li>
                <Link
                  to={getLocalePath("/free-ai-text-watermark-remover")}
                  className="text-neutral-600 dark:text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors"
                  aria-label="Free AI Text Watermark Remover"
                >
                  {getText("freeAIText")}
                </Link>
              </li>
              <li>
                <Link
                  to={getLocalePath("/free-ai-watermark-remover")}
                  className="text-neutral-600 dark:text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors"
                  aria-label="Free AI Watermark Remover"
                >
                  {getText("freeAI")}
                </Link>
              </li>
              <li>
                <Link
                  to={getLocalePath("/free-gpt-watermark-remover")}
                  className="text-neutral-600 dark:text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors"
                  aria-label="Free GPT Watermark Remover"
                >
                  {getText("freeGPT")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Resources Column */}
          <div>
            <h2 className="font-semibold text-neutral-900 dark:text-white mb-4">
              {getText("resources")}
            </h2>
            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  to={getLocalePath("/blog")}
                  className="text-neutral-600 dark:text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors"
                  aria-label="Blog"
                >
                  {getText("blog")}
                </Link>
              </li>
              <li>
                <Link
                  to={getLocalePath("/knowledge")}
                  className="text-neutral-600 dark:text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors"
                  aria-label="Knowledge Base"
                >
                  {getText("knowledge")}
                </Link>
              </li>
              <li>
                <Link
                  to={getLocalePath("/about")}
                  className="text-neutral-600 dark:text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors"
                  aria-label="About"
                >
                  {getText("about")}
                </Link>
              </li>
              <li>
                <Link
                  to={getLocalePath("/pricing")}
                  className="text-neutral-600 dark:text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors"
                  aria-label="View Pricing"
                >
                  {getText("pricing")}
                </Link>
              </li>
              <li>
                <Link
                  to={getLocalePath("/faq")}
                  className="text-neutral-600 dark:text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors"
                  aria-label="View FAQ"
                >
                  {getText("faq")}
                </Link>
              </li>
              <li>
                <Link
                  to={getLocalePath("/comparison")}
                  className="text-neutral-600 dark:text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors"
                  aria-label="Learn why to remove watermarks"
                >
                  {getText("comparison")}
                </Link>
              </li>
              <li>
                <Link
                  to={getLocalePath("/feature-request")}
                  className="text-neutral-600 dark:text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors"
                  aria-label="Request a feature"
                >
                  {getText("featureRequest")}
                </Link>
              </li>
              <li>
                <Link
                  to={getLocalePath("/docs")}
                  className="text-neutral-600 dark:text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors"
                  aria-label="Developer Documentation"
                >
                  {getText("devDocs")}
                </Link>
              </li>
            </ul>
            {/* API Subsection */}
            <h3 className="font-semibold text-neutral-900 dark:text-white mt-6 mb-3 text-sm">
              {getText("api")}
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  to={getLocalePath("/api")}
                  className="text-neutral-600 dark:text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors"
                  aria-label="API Overview"
                >
                  {getText("apiOverview")}
                </Link>
              </li>
              <li>
                <Link
                  to={getLocalePath("/api/writing-tools")}
                  className="text-neutral-600 dark:text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors"
                  aria-label="API for Writing Tools"
                >
                  {getText("apiWritingTools")}
                </Link>
              </li>
              <li>
                <Link
                  to={getLocalePath("/api/chatbot-middleware")}
                  className="text-neutral-600 dark:text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors"
                  aria-label="API for Chatbots"
                >
                  {getText("apiChatbots")}
                </Link>
              </li>
              <li>
                <Link
                  to={getLocalePath("/api/content-platforms")}
                  className="text-neutral-600 dark:text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors"
                  aria-label="API for Content Platforms"
                >
                  {getText("apiContentPlatforms")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal Column */}
          <div>
            <h2 className="font-semibold text-neutral-900 dark:text-white mb-4">
              {getText("legal")}
            </h2>
            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  to={getLocalePath("/privacy")}
                  className="text-neutral-600 dark:text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors"
                  aria-label="View Privacy Policy"
                >
                  {getText("privacy")}
                </Link>
              </li>
              <li>
                <Link
                  to={getLocalePath("/terms")}
                  className="text-neutral-600 dark:text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors"
                  aria-label="View Terms of Service"
                >
                  {getText("terms")}
                </Link>
              </li>
              <li>
                <Link
                  to={getLocalePath("/imprint")}
                  className="text-neutral-600 dark:text-neutral-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors"
                  aria-label="View Imprint"
                >
                  {getText("imprint")}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-neutral-200 dark:border-charcoal-700/50">
          <div className="flex flex-col items-center gap-4">
            <p className="text-center text-sm text-neutral-500 dark:text-neutral-400">
              © {new Date().getFullYear()} GPT Watermark-Remover.{" "}
              {getText("allRightsReserved")}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
});

Footer.displayName = "Footer";

export default Footer;
